// "Was this helpful?" feedback widget with thumbs up/down pill buttons
// On No: notifies parent to reveal the support escalation CTA (no text input collected)

import { useState } from 'react';
import { Box, Typography } from '@/ui';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';

interface FeedbackWidgetProps {
  onNegativeFeedback?: () => void;
  onPositiveFeedback?: () => void;
}

const COLORS = {
  accent: '#004b49',
  accentBg: '#e7f5f4',
  accentBgHover: '#d3ece9',
  borderAccent: '#b8e3df',
  bgInput: '#ffffff',
  bgSubtle: '#f3f4f6',
  border: '#e5e7eb',
  textSecondary: '#4b5563',
  textMuted: '#9ca3af',
};

function FeedbackWidget({ onNegativeFeedback, onPositiveFeedback }: FeedbackWidgetProps) {
  const [feedbackGiven, setFeedbackGiven] = useState<'positive' | 'negative' | null>(null);

  const handleNo = () => {
    if (feedbackGiven === 'negative') return;
    setFeedbackGiven('negative');
    if (onNegativeFeedback) {
      onNegativeFeedback();
    }
  };

  const handleYes = () => {
    if (feedbackGiven === 'positive') return;
    setFeedbackGiven('positive');
    if (onPositiveFeedback) {
      onPositiveFeedback();
    }
  };

  return (
    <Box data-testid="features-support-faqs-components-feedback-widget-container-action-1"
      sx={{
        py: 3,
        mb: 3,
        borderTop: `1px solid ${COLORS.border}`,
        borderBottom: `1px solid ${COLORS.border}`,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <Typography data-testid="features-support-faqs-components-feedback-widget-text-was-this-helpful?"
        sx={{
          fontSize: '13px',
          fontWeight: 500,
          color: COLORS.textSecondary,
          mb: 1.5,
          fontFamily: 'Inter, sans-serif',
        }}
      >
        Was this helpful?
      </Typography>
      <Box data-testid="features-support-faqs-components-feedback-widget-container-action-2" sx={{ display: 'flex', gap: 1, mb: feedbackGiven ? 2 : 0 }}>
        <Box data-testid="features-support-faqs-components-feedback-widget-container-handle-yes"
          component="button"
          onClick={handleYes}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor:
              feedbackGiven === 'positive'
                ? COLORS.accentBgHover
                : COLORS.bgInput,
            color: feedbackGiven === 'positive' ? COLORS.accent : COLORS.textSecondary,
            border: `1px solid ${
              feedbackGiven === 'positive'
                ? COLORS.accent
                : COLORS.border
            }`,
            transition: 'transform 0.1s',
            fontFamily: 'Inter, sans-serif',
            '&:hover': { transform: 'scale(1.04)' },
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <ThumbUpOutlinedIcon sx={{ fontSize: 15 }} />
          <Typography data-testid="features-support-faqs-components-feedback-widget-text-yes" sx={{ fontSize: '13px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
            Yes
          </Typography>
        </Box>
        <Box data-testid="features-support-faqs-components-feedback-widget-container-handle-no"
          component="button"
          onClick={handleNo}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 1,
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: feedbackGiven === 'negative' ? COLORS.bgSubtle : COLORS.bgInput,
            color: feedbackGiven === 'negative' ? COLORS.accent : COLORS.textSecondary,
            border: `1px solid ${feedbackGiven === 'negative' ? COLORS.accent : COLORS.border}`,
            transition: 'transform 0.1s',
            fontFamily: 'Inter, sans-serif',
            '&:hover': { transform: 'scale(1.04)' },
            '&:active': { transform: 'scale(0.96)' },
          }}
        >
          <ThumbDownOutlinedIcon sx={{ fontSize: 15 }} />
          <Typography data-testid="features-support-faqs-components-feedback-widget-text-no" sx={{ fontSize: '13px', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
            No
          </Typography>
        </Box>
      </Box>

      {feedbackGiven === 'positive' && (
        <Typography data-testid="features-support-faqs-components-feedback-widget-text-thanks-for-your-feedback!"
          sx={{ fontSize: '13px', color: COLORS.accent, fontFamily: 'Inter, sans-serif' }}
        >
          Thanks for your feedback!
        </Typography>
      )}
    </Box>
  );
}

export default FeedbackWidget;
